// Calculator.js
import React, { useState } from 'react';
import { combination, InfoTextField } from './Util';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';

function calculateLootChances(
  baseRate,
  monstersInt,
  cap,
) {
  let result = [];
  if (!isNaN(monstersInt) && monstersInt > 0) {
    const p = baseRate / 100;
    for (let k = 0; k <= monstersInt; k++) {
      const prob =
        combination(monstersInt, k) *
        Math.pow(p, k) *
        Math.pow(1 - p, monstersInt - k);
      if (k > cap) {
        result[result.length - 1].prob += prob;
        continue;
      }
      result.push({ k, prob });
    }
  }
  return result;
}

function mergeLootChances(
  lootChancesA,
  lootChancesB,
) {
  let result = [];
  for (let k = 0; k <= lootChancesA.length - 1 + lootChancesB.length - 1; k++) {
    const prob = 0;
    result.push({ k, prob});
  }
  lootChancesA.forEach(({ k: kA, prob: probA }) => {
    lootChancesB.forEach(({ k: kB, prob: probB }) => {
      const k = kA + kB;
      const prob = probA * probB;
      result[k].prob += prob;
    });
  });
  return result;
}

function expectedValue(lootChances) {
  let result = 0;
  lootChances.forEach(({ k, prob}) => {
    result += k * prob;
  })
  return result;
}

function lootRow(lootChances, tableCellSx) {
  return lootChances.map(({ k, prob }) => (
    <TableCell
      align="center"
      key={`prob-${k}-none`}
      sx={tableCellSx}
    >
      {(prob * 100).toFixed(2)}%
    </TableCell>
  ));
}

function fragmentsRow(elixir, cost, fragments, duration, fragmentsRotation, durationRotation, profit, tableCellSx){
  return (
    <TableRow>
      <TableCell
        align="center"
        key={`fragment-elixir-${elixir}`}
        sx={tableCellSx}
      >
        {elixir}
      </TableCell>
      <TableCell
        align="center"
        key={`fragment-cost-${elixir}`}
        sx={tableCellSx}
      >
        {cost} K
      </TableCell>
      <TableCell
        align="center"
        key={`fragment-fragments-${elixir}`}
        sx={tableCellSx}
      >
        {fragments}
      </TableCell>
      <TableCell
        align="center"
        key={`fragment-duration-${elixir}`}
        sx={tableCellSx}
      >
        {duration}
      </TableCell>
      <TableCell
        align="center"
        key={`fragment-fragments-rotation-${elixir}`}
        sx={tableCellSx}
      >
        {fragmentsRotation}
      </TableCell>
      <TableCell
        align="center"
        key={`fragment-duration-rotation-${elixir}`}
        sx={tableCellSx}
      >
        {durationRotation}
      </TableCell>
      <TableCell
        align="center"
        key={`fragment-profit-${elixir}`}
        sx={tableCellSx}
      >
        {profit} K/min
      </TableCell>
    </TableRow>
    );
}

function FragmentsCalculator() {
  // Default values
  const [monsters, setMonsters] = useState('6'); // Number of monsters in fight
  const [fragmentValue, setFragmentValue] = useState('17000');
  const [fightDuration, setFightDuration] = useState('2');
  const [characters, setCharacters] = useState('4');

  // Compute loot chances using the binomial distribution.
  const monstersInt = parseInt(monsters, 10);
  let lootChances = calculateLootChances(1, monstersInt, Infinity);
  let minorLootChances = calculateLootChances(5, monstersInt, 3);
  minorLootChances = mergeLootChances(lootChances, minorLootChances);
  let enhancedLootChances = calculateLootChances(10, monstersInt, 3);
  enhancedLootChances = mergeLootChances(lootChances, enhancedLootChances);
  let majorLootChances = calculateLootChances(20, monstersInt, 3);
  majorLootChances = mergeLootChances(lootChances, majorLootChances);

  const fragmentValueInt = parseInt(fragmentValue, 10);
  const minorCost = fragmentValueInt * 5;
  const enhancedCost = fragmentValueInt * 10;
  const majorCost = fragmentValueInt * 15;

  const fightDurationFloat = parseFloat(fightDuration);
  const charactersInt = parseInt(characters, 10);

  // Common sx style for TextFields: border color = header color (#333), text white.
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#333',
      },
      '&:hover fieldset': {
        borderColor: '#333',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#333',
      },
    },
    input: {
      color: '#fff',
    },
    label: {
      color: '#fff',
    },
  };

  const tableCellSx = {
    border: '1px solid #333',
    color: '#fff',
  };

  return (
    <Box>
      {lootChances !== [] && (
        <Box sx={{ mt: 3 }}>
          <InfoTextField
            fullWidth
            label="Number of Monsters in Fight"
            tooltip="Enter the total number of monsters that drop the loot you want in current combat."
            type="number"
            value={monsters}
            onChange={(e) => setMonsters(e.target.value)}
            sx={{ mt: 2, ...textFieldSx }}
          />
          <TableContainer
            component={Paper}
            sx={{
              mt: 3,
              backgroundColor: '#474747',
              border: '1px solid #333',
            }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    key={`loot-elixir`}
                    sx={tableCellSx}
                  >
                    elixir
                  </TableCell>
                  {minorLootChances.map(({ k }) => (
                    <TableCell
                      align="center"
                      key={`loot-${k}`}
                      sx={tableCellSx}
                    >
                      {k}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="center"
                    key={`loot-elixir-none`}
                    sx={tableCellSx}
                  >
                    none
                  </TableCell>
                  {lootRow(lootChances, tableCellSx)}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="center"
                    key={`loot-elixir-minor`}
                    sx={tableCellSx}
                  >
                    minor
                  </TableCell>
                  {lootRow(minorLootChances, tableCellSx)}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="center"
                    key={`loot-elixir-enhanced`}
                    sx={tableCellSx}
                  >
                    enhanced
                  </TableCell>
                  {lootRow(enhancedLootChances, tableCellSx)}
                </TableRow>
                <TableRow>
                  <TableCell
                    align="center"
                    key={`loot-elixir-major`}
                    sx={tableCellSx}
                  >
                    major
                  </TableCell>
                  {lootRow(majorLootChances, tableCellSx)}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <InfoTextField
            fullWidth
            label="Fragment Value (K)"
            tooltip="Current Value for a Fragment."
            type="number"
            value={fragmentValue}
            onChange={(e) => setFragmentValue(e.target.value)}
            sx={{ mt: 2, ...textFieldSx }}
          />
          <InfoTextField
            fullWidth
            label="Fight Duration (min)"
            tooltip="Duration for each Fight (including search)."
            type="number"
            value={fightDuration}
            onChange={(e) => setFightDuration(e.target.value)}
            sx={{ mt: 2, ...textFieldSx }}
          />
          <InfoTextField
            fullWidth
            label="Number of Characters"
            tooltip="Number of Characters in the elixir rotation."
            type="number"
            value={characters}
            onChange={(e) => setCharacters(e.target.value)}
            sx={{ mt: 2, ...textFieldSx }}
          />
          <TableContainer
            component={Paper}
            sx={{
              mt: 3,
              backgroundColor: '#474747',
              border: '1px solid #333',
            }}
          >
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    align="center"
                    key={`fragment-elixir`}
                    sx={tableCellSx}
                  >
                    elixir
                  </TableCell>
                  <TableCell
                    align="center"
                    key={`fragment-cost`}
                    sx={tableCellSx}
                  >
                    cost
                  </TableCell>
                  <TableCell
                    align="center"
                    key={`fragment-fragments`}
                    sx={tableCellSx}
                  >
                    fragments
                  </TableCell>
                  <TableCell
                    align="center"
                    key={`fragment-duration`}
                    sx={tableCellSx}
                  >
                    duration
                  </TableCell>
                  <TableCell
                    align="center"
                    key={`fragments-fragments-rotation`}
                    sx={tableCellSx}
                  >
                    fragments (rotation)
                  </TableCell>
                  <TableCell
                    align="center"
                    key={`fragments-duration-rotation`}
                    sx={tableCellSx}
                  >
                    duration (rotation)
                  </TableCell>
                  <TableCell
                    align="center"
                    key={`fragments-profit`}
                    sx={tableCellSx}
                  >
                    profit/duration
                  </TableCell>
                </TableRow>
                {fragmentsRow(
                  "none",
                  "0",
                  "N/A",
                  "N/A",
                  "N/A",
                  "N/A",
                  (expectedValue(lootChances) * fragmentValueInt / fightDurationFloat).toFixed(2),
                  tableCellSx
                )}
                {fragmentsRow(
                  "minor",
                  minorCost,
                  (expectedValue(minorLootChances) * 15).toFixed(2),
                  `${fightDurationFloat * 15} min`,
                  (expectedValue(minorLootChances) * 15 * charactersInt).toFixed(2),
                  `${fightDurationFloat * 15 * charactersInt} min`,
                  ((expectedValue(minorLootChances) * 15 * charactersInt * fragmentValueInt - minorCost) / (fightDurationFloat * 15 * charactersInt)).toFixed(2),
                  tableCellSx
                )}
                {fragmentsRow(
                  "enhanced",
                  enhancedCost,
                  (expectedValue(enhancedLootChances) * 10).toFixed(2),
                  `${fightDurationFloat * 10} min`,
                  (expectedValue(enhancedLootChances) * 10 * charactersInt).toFixed(2),
                  `${fightDurationFloat * 10 * charactersInt} min`,
                  ((expectedValue(enhancedLootChances) * 10 * charactersInt * fragmentValueInt - enhancedCost) / (fightDurationFloat * 10 * charactersInt)).toFixed(2),
                  tableCellSx
                )}
                {fragmentsRow(
                  "major",
                  majorCost,
                  (expectedValue(majorLootChances) * 5).toFixed(2),
                  `${fightDurationFloat * 5} min`,
                  (expectedValue(majorLootChances) * 5 * charactersInt).toFixed(2),
                  `${fightDurationFloat * 5 * charactersInt} min`,
                  ((expectedValue(majorLootChances) * 5 * charactersInt * fragmentValueInt - majorCost) / (fightDurationFloat * 5 * charactersInt)).toFixed(2),
                  tableCellSx
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}

export default FragmentsCalculator;
